/**
 * ReportCardGrid Component
 *
 * Card-grid view for the reports list page. Mirrors the visual language of
 * ClientCardGrid exactly: rounded-[22px] cards with 0.5px borders, a
 * 3-column responsive grid, gap-4 spacing, p-0 CardContent, header row with
 * an icon-badge + title + status chip, a 0.5px horizontal divider, body
 * section with date range and PDF status, and a footer row with
 * Created/Updated timestamps plus a dropdown action menu.
 */

import {
  Card,
  CardContent,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import {
  DotsThreeVertical,
  PencilSimple,
  CopySimple,
  DownloadSimple,
  Trash,
  Slideshow,
  CalendarDots,
  FilePdf,
  PaperPlaneTilt,
  UserPlus,
  CheckCircle,
  ChatText,
  ArrowUUpLeft,
  ClockCounterClockwise,
} from "@phosphor-icons/react";
import { ClientAvatar } from "../clients/ClientAvatar";
import type { ReportListItem } from "./ReportTableView";
import { APPROVAL_STATUS_STYLES } from "./ReportTableView";
import type { ReviewAction } from "./ReviewActionDialog";


// ============================================================================
// Helpers (kept in sync with ReportTableView/index.tsx)
// ============================================================================

const STATUS_STYLES: Record<
  ReportListItem["status"],
  { label: string; className: string }
> = {
  draft: {
    label: "Draft",
    className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  },
  ready: {
    label: "Ready",
    className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  },
};

function formatDateRange(start: string, end: string): string {
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  const opts: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  return `${s.toLocaleDateString("en-US", opts)} – ${e.toLocaleDateString("en-US", opts)}`;
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ============================================================================
// Props
// ============================================================================

interface ReportCardGridProps {
  reports: ReportListItem[];
  canManageReports: boolean;
  onReportClick?: (report: ReportListItem) => void;
  onEditClick?: (report: ReportListItem) => void;
  onDuplicateClick?: (report: ReportListItem) => void;
  onDownloadClick?: (report: ReportListItem) => void;
  onDeleteClick?: (report: ReportListItem) => void;
  onSubmitClick?: (report: ReportListItem) => void;
  onAssignClick?: (report: ReportListItem) => void;
  onReviewClick?: (report: ReportListItem, action: ReviewAction) => void;
  onHistoryClick?: (report: ReportListItem) => void;
  triggerRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
}

// ============================================================================
// Component
// ============================================================================

export function ReportCardGrid({
  reports,
  canManageReports,
  onReportClick,
  onEditClick,
  onDuplicateClick,
  onDownloadClick,
  onDeleteClick,
  onSubmitClick,
  onAssignClick,
  onReviewClick,
  onHistoryClick,
  triggerRefs,
}: ReportCardGridProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {reports.map((report) => {
        const FormatIcon = report.format === "slide-deck" ? Slideshow : FilePdf;
        const statusStyle = STATUS_STYLES[report.status];

        return (
          <Card
            key={report.id}
            data-testid="report-card"
            className={`shadow-xs hover:shadow-sm transition-shadow ${onReportClick ? "cursor-pointer" : ""} !rounded-[22px]`}
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={() => onReportClick?.(report)}
            role={onReportClick ? "link" : undefined}
            tabIndex={onReportClick ? 0 : undefined}
            onKeyDown={(e) => {
              if (onReportClick && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onReportClick(report);
              }
            }}
          >
            <CardContent className="p-0">
              {/* Top: name + status chip */}
              <div className="flex items-center gap-3 px-4 pt-4 pb-3">
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <h3 className="text-[14px] font-medium truncate min-w-0">
                    {report.name}
                  </h3>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.className}`}
                  >
                    {statusStyle.label}
                  </span>
                  {report.approvalStatus &&
                    APPROVAL_STATUS_STYLES[report.approvalStatus] && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${APPROVAL_STATUS_STYLES[report.approvalStatus].className}`}
                      >
                        {APPROVAL_STATUS_STYLES[report.approvalStatus].label}
                      </span>
                    )}
                  {report.reviewerId &&
                    (report.reviewerName || report.reviewerEmail) && (
                      <span className="text-xs text-muted-foreground">
                        · {report.reviewerName ?? report.reviewerEmail}
                      </span>
                    )}
                </div>
              </div>

              {/* Divider */}
              <div
                className="mx-4"
                style={{ borderTop: "0.5px solid hsl(var(--input) / 0.5)" }}
              />

              {/* Body: format + date range + client */}
              <div className="pt-3 pb-3 px-4 space-y-3">
                <div className="flex items-center flex-wrap gap-3">
                  <span className="inline-flex items-center gap-1.5 text-[13px] text-foreground">
                    <FormatIcon size={18} />
                    {report.format === "slide-deck" ? "Slide Deck" : "Document"}
                  </span>
                  <span
                    className="w-px h-3.5"
                    style={{ background: "hsl(var(--input) / 0.3)" }}
                  />
                  <span className="inline-flex items-center gap-1.5 text-[13px] text-foreground">
                    <CalendarDots size={18} />
                    {formatDateRange(report.dateRangeStart, report.dateRangeEnd)}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <ClientAvatar
                    name={report.clientName}
                    logoUrl={report.clientLogoUrl ?? null}
                    size="sm"
                  />
                  <span className="text-[13px] text-foreground truncate">
                    {report.clientName}
                  </span>
                </div>

                {/* Footer: Created + Updated + actions */}
                <div className="flex items-center gap-3 text-[13px] text-foreground -mt-1">
                  <span>Created {formatTimestamp(report.createdAt)}</span>
                  <span
                    className="w-px h-3.5"
                    style={{ background: "hsl(var(--input) / 0.3)" }}
                  />
                  <span>Updated {formatTimestamp(report.updatedAt)}</span>
                  {canManageReports && (
                    <div
                      className="ml-auto"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            ref={(el) => {
                              triggerRefs.current[report.id] = el;
                            }}
                            variant="ghost"
                            size="icon"
                            className="min-h-8 min-w-8 cursor-pointer rounded-lg border border-transparent hover:bg-background hover:border-input transition-all"
                            aria-label={`Actions for ${report.name}`}
                          >
                            <DotsThreeVertical size={22} weight="bold" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="rounded-xl p-2 shadow-md"
                          style={{ border: "0.5px solid var(--hairline)" }}
                        >
                          <DropdownMenuItem
                            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                            onClick={() => onEditClick?.(report)}
                          >
                            <PencilSimple size={14} className="mr-1" />
                            Edit Report
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                            onClick={() => onSubmitClick?.(report)}
                          >
                            <PaperPlaneTilt size={14} className="mr-1" />
                            {report.approvalStatus === "changes_requested" ||
                            report.approvalStatus === "withdrawn"
                              ? "Resubmit for review"
                              : "Submit for review"}
                          </DropdownMenuItem>
                          {report.approvalStatus && (
                            <DropdownMenuItem
                              className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                              onClick={() => onAssignClick?.(report)}
                            >
                              <UserPlus size={14} className="mr-1" />
                              Assign reviewer
                            </DropdownMenuItem>
                          )}
                          {report.approvalStatus === "pending_review" && (
                            <>
                              <DropdownMenuItem
                                className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                                onClick={() => onReviewClick?.(report, "approve")}
                              >
                                <CheckCircle size={14} className="mr-1" />
                                Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                                onClick={() => onReviewClick?.(report, "request_changes")}
                              >
                                <ChatText size={14} className="mr-1" />
                                Request changes
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                                onClick={() => onReviewClick?.(report, "withdraw")}
                              >
                                <ArrowUUpLeft size={14} className="mr-1" />
                                Withdraw
                              </DropdownMenuItem>
                            </>
                          )}
                          {report.approvalStatus && (
                            <DropdownMenuItem
                              className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                              onClick={() => onHistoryClick?.(report)}
                            >
                              <ClockCounterClockwise size={14} className="mr-1" />
                              Approval history
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                            onClick={() => onDuplicateClick?.(report)}
                          >
                            <CopySimple size={14} className="mr-1" />
                            Duplicate
                          </DropdownMenuItem>
                          {report.pdfUrl && (
                            <DropdownMenuItem
                              className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                              onClick={() => onDownloadClick?.(report)}
                            >
                              <DownloadSimple size={14} className="mr-1" />
                              Download PDF
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="min-h-11 text-[13px] text-destructive cursor-pointer rounded-lg hover:!bg-destructive/10 focus:!bg-destructive/10 focus:!text-destructive focus:!border-destructive/30"
                            onClick={() => onDeleteClick?.(report)}
                          >
                            <Trash size={14} className="mr-1" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
